import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * GL.js — Smart Sales System product page
 * Content sourced 1:1 from "SMART SALES SYSTEM — OVERVIEW" (6 pages)
 *
 * Drop-in page component for a MERN app.
 * No external UI libraries required — styles are scoped in the injected
 * <style> tag below (id="gl-styles"), so this file is self-contained.
 *
 * Usage (React Router example):
 *   import GL from "./pages/GL";
 *   <Route path="/smart-crm" element={<GL />} />
 */

const SECTIONS = [
  {
    num: "01",
    sidebarTitle: "THE PAIN",
    sidebarBody: "No follow up means no money.",
    title: "Money Falling Through Cracks.",
    redHeadline:
      "You get leads, but you forget to follow up. They go cold. You lose the job.",
    bullets: [
      "Leads sit in your inbox.",
      "You forget who you called.",
      "Customers go to the guy who texts back fastest.",
    ],
    boxTitle: "THE PROBLEM",
    boxBody:
      "You are working hard to get leads, but your follow-up is broken.",
    isLast: false,
  },
  {
    num: "02",
    sidebarTitle: "THE FIX",
    sidebarBody: "Everything in one simple place.",
    title: "A System That Follows Up For You.",
    redHeadline:
      "We give you a smart system that automatically texts and emails your leads until they buy.",
    bullets: [
      "It texts leads back instantly.",
      "It keeps all your customers in one place.",
      "You never lose a lead again.",
    ],
    boxTitle: "THE SOLUTION",
    boxBody:
      "The system chases them for you. You just close the deal.",
    isLast: false,
  },
  {
    num: "03",
    sidebarTitle: "OUTREACH",
    sidebarBody: "Reach thousands in one click.",
    title: "Blast Your Offer To Everyone.",
    redHeadline:
      "Send one message to thousands of people with one click.",
    bullets: [
      "Send bulk SMS texts to past clients.",
      "Send email blasts with new offers.",
      "Drop voicemails without their phone ringing.",
    ],
    boxTitle: "THE OUTREACH",
    boxBody:
      "Reactivate old customers and turn them into new money instantly.",
    isLast: false,
  },
  {
    num: "04",
    sidebarTitle: "SOCIALS",
    sidebarBody: "Post everywhere at once.",
    title: "Look Busy On Social Media.",
    redHeadline:
      "The system posts to Facebook, Instagram, and Google for you.",
    bullets: [
      "Schedule a month of posts in 10 minutes.",
      "Look professional to new customers.",
      "Never forget to post again.",
    ],
    boxTitle: "THE BRAND",
    boxBody:
      "Customers trust businesses that look active online. We automate it.",
    isLast: false,
  },
  {
    num: "05",
    sidebarTitle: "REVIEWS",
    sidebarBody: "Build trust without asking.",
    title: "Get 5-Star Reviews Automatically.",
    redHeadline:
      "When you finish a job, the system asks the customer for a review.",
    bullets: [
      "It texts them a simple link.",
      "Bad reviews get sent to you privately.",
      "Good reviews go straight to Google.",
    ],
    boxTitle: "THE REPUTATION",
    boxBody:
      "Dominate your local area by having the most 5-star reviews.",
    isLast: false,
  },
  {
    num: "06",
    sidebarTitle: "THE OFFER",
    sidebarBody: "We waived the $397 setup fee for you today.",
    title: "Close More Jobs Starting Today.",
    redHeadline:
      "Stop losing money to bad follow-up. Let the system do the work.",
    bullets: null,
    boxTitle: "SECRET COUPON",
    boxBody:
      "Want 20-30% off the monthly price? We only have 5 coupons left. Apply now to see if you qualify.",
    isLast: true,
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("gl-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("gl-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Section({ section }) {
  const ref = useReveal();
  return (
    <div className="gl-section gl-reveal" ref={ref}>
      {/* Large title */}
      <div className="gl-title-area">
        <h2 className="gl-title">{section.title}</h2>
      </div>

      {/* Divider */}
      <div className="gl-divider" />

      {/* Two-column body */}
      <div className="gl-body-grid">
        {/* Left column */}
        <div className="gl-main-col">
          <h3 className="gl-red-headline">{section.redHeadline}</h3>

          {/* Standard bullet list */}
          {section.bullets && (
            <ul className="gl-bullets">
              {section.bullets.map((b) => (
                <li key={b} className="gl-bullet">
                  <span className="gl-sq" aria-hidden="true" />
                  <span className="gl-bullet-text">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Pricing bullets — section 6 only */}
          {section.isLast && (
            <ul className="gl-bullets">
              <li className="gl-bullet">
                <span className="gl-sq" aria-hidden="true" />
                <span className="gl-bullet-text">
                  Setup Fee:{" "}
                  <span className="gl-strikethrough">$397</span>{" "}
                  <span className="gl-waived">WAIVED</span>
                </span>
              </li>
              <li className="gl-bullet">
                <span className="gl-sq" aria-hidden="true" />
                <span className="gl-bullet-text">Monthly: $297</span>
              </li>
            </ul>
          )}

          {/* Red box */}
          <div className="gl-red-box">
            <span className="gl-box-title">{section.boxTitle}</span>
            <p className="gl-box-body">{section.boxBody}</p>
            {section.isLast && (
              <Link to="/booking" className="gl-cta-btn">
                CLAIM YOUR TERRITORY →
              </Link>
            )}
          </div>
        </div>

        {/* Vertical rule */}
        <div className="gl-vrule" aria-hidden="true" />

        {/* Right sidebar */}
        <div className="gl-sidebar">
          <div className="gl-sidebar-num">{section.num}</div>
          <div className="gl-sidebar-label">{section.sidebarTitle}</div>
          <p className="gl-sidebar-body">{section.sidebarBody}</p>
        </div>
      </div>
    </div>
  );
}

export default function GL() {
  return (
    <div className="gl-root">
      <style id="gl-styles">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* ===== ROOT ===== */
        .gl-root {
          background: #ffffff;
          color: #111111;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }
        .gl-root * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ===== REVEAL ===== */
        .gl-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.65s ease, transform 0.65s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .gl-reveal.gl-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ===== SECTION ===== */
        .gl-section {
          max-width: 900px;
          margin: 0 auto;
          padding: 80px 40px;
          border-bottom: 1px solid #e8e8e8;
        }
        .gl-section:last-child {
          border-bottom: none;
        }

        /* ===== TITLE ===== */
        .gl-title-area {
          padding-bottom: 36px;
        }
        .gl-title {
          font-size: clamp(36px, 6vw, 64px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #111111;
          max-width: 14ch;
        }

        /* ===== DIVIDER ===== */
        .gl-divider {
          height: 1px;
          background: #111111;
          margin-bottom: 48px;
        }

        /* ===== BODY GRID ===== */
        .gl-body-grid {
          display: grid;
          grid-template-columns: 1fr 1px 220px;
          gap: 0 40px;
          align-items: start;
        }

        /* ===== MAIN COLUMN ===== */
        .gl-main-col {
          padding-right: 8px;
        }
        .gl-red-headline {
          font-size: clamp(20px, 2.8vw, 26px);
          font-weight: 700;
          line-height: 1.25;
          color: #d0201a;
          margin-bottom: 28px;
        }

        /* ===== BULLETS ===== */
        .gl-bullets {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 32px;
        }
        .gl-bullet {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .gl-sq {
          flex-shrink: 0;
          width: 12px;
          height: 12px;
          background: #d0201a;
          margin-top: 4px;
        }
        .gl-bullet-text {
          font-size: 15px;
          font-weight: 700;
          color: #111111;
          line-height: 1.5;
        }

        /* Pricing specials */
        .gl-strikethrough {
          text-decoration: line-through;
          color: #888888;
          font-weight: 700;
        }
        .gl-waived {
          color: #d0201a;
          font-weight: 700;
        }

        /* ===== RED BOX ===== */
        .gl-red-box {
          background: #d0201a;
          padding: 24px 28px 28px;
        }
        .gl-box-title {
          display: block;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #ffffff;
          margin-bottom: 12px;
        }
        .gl-box-body {
          font-size: 15px;
          font-weight: 700;
          line-height: 1.6;
          color: #ffffff;
        }

        /* ===== CTA BUTTON ===== */
        .gl-cta-btn {
          display: inline-block;
          margin-top: 20px;
          background: #ffffff;
          color: #111111;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          text-decoration: none;
          padding: 13px 24px;
          transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .gl-cta-btn:hover {
          background: #111111;
          color: #ffffff;
          transform: translateY(-1px);
        }
        .gl-cta-btn:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        /* ===== VERTICAL RULE ===== */
        .gl-vrule {
          width: 1px;
          background: #cccccc;
          align-self: stretch;
          min-height: 180px;
        }

        /* ===== SIDEBAR ===== */
        .gl-sidebar {
          padding-left: 8px;
        }
        .gl-sidebar-num {
          font-size: clamp(64px, 9vw, 96px);
          font-weight: 900;
          line-height: 0.9;
          color: #d0201a;
          letter-spacing: -0.03em;
          margin-bottom: 14px;
        }
        .gl-sidebar-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #111111;
          margin-bottom: 12px;
        }
        .gl-sidebar-body {
          font-size: 14px;
          line-height: 1.7;
          color: #555555;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 700px) {
          .gl-section { padding: 56px 20px; }

          .gl-body-grid {
            grid-template-columns: 1fr;
            gap: 36px 0;
          }
          .gl-vrule { display: none; }
          .gl-sidebar {
            padding-left: 0;
            border-top: 1px solid #e0e0e0;
            padding-top: 28px;
          }
          .gl-sidebar-num { font-size: 72px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .gl-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {SECTIONS.map((section) => (
        <Section section={section} key={section.num} />
      ))}
    </div>
  );
}
